import React from "react"
import { actionKeys } from "@love-irish/i18n"
import { IconMusic, IconVinyl, IconFileText } from "@tabler/icons-react"
import routes from "@love-irish/routes"
import BaseListItem from "../base"

export interface SongListItemProps {
  item: Record<string, any>
  className?: string
  /** Custom footer content to replace the default "Added by..." text */
  footer?: React.ReactNode
}

export const SongListItem: React.FC<SongListItemProps> = ({
  item,
  className,
  footer,
}) => {
  // Byline: "Kneecap · 2017". Drop the artist when it's just "Traditional" and the flag is set — the pill
  // already says so, and we don't want it twice.
  const showArtist =
    item.artist &&
    !(item.traditional && item.artist.toLowerCase() === "traditional")
  const byline = [showArtist ? item.artist : null, item.releaseYear]
    .filter(Boolean)
    .join(" · ")
  const recordingCount = item.recordings?.length ?? 0

  return (
    <BaseListItem
      item={item}
      className={className}
      footer={footer}
      typeIcon={IconMusic}
      typeLabel="Song"
      testId="song-list-item"
      readMore={{ useI18n: true, actionKey: actionKeys.LISTEN }}
      media={
        (item.imageUrl || item.thumbnailUrl) && (
          <a href={item.path} className="relative block shrink-0">
            {/* Mic/vinyl overlay hints "this is playable" over the cover art. */}
            <IconMusic className="absolute top-1/2 left-1/2 -translate-1/2 text-white/80" />
            <img loading="lazy"
              src={item.thumbnailUrl || item.imageUrl}
              alt={item.name}
              className="max-w-[200px] @max-sm/list-item:max-w-[100px] rounded-xl"
            />
          </a>
        )
      }
      meta={
        (recordingCount > 0 || (item.lyrics && item.slug)) && (
          <div className="flex items-center gap-4 text-xs text-dark/60 dark:text-white/60">
            {recordingCount > 0 && (
              <span className="flex items-center gap-1">
                <IconVinyl size={14} />
                {recordingCount} {recordingCount === 1 ? "version" : "versions"}
              </span>
            )}
            {item.lyrics && item.slug && (
              // Links straight to the song's Lyrics tab (the interactive line-by-line player).
              <a
                href={routes.libraryResourceLyricsPath(
                  item.slug,
                  item.libraryType
                )}
                className="flex items-center gap-1 hover:text-primary-600 hover:underline dark:hover:text-primary-300"
              >
                <IconFileText size={14} />
                Lyrics
              </a>
            )}
          </div>
        )
      }
    >
      {(byline || item.traditional) && (
        <div className="mt-3 flex items-center gap-2 text-sm text-dark/70 dark:text-white/70">
          {byline && <span>{byline}</span>}
          {item.traditional && (
            <span className="rounded-full bg-primary-500/10 px-2 py-0.5 text-xs font-medium text-primary-600 dark:text-primary-300">
              Traditional
            </span>
          )}
        </div>
      )}
    </BaseListItem>
  )
}

export default SongListItem
